import { z } from "zod";

import { PersonalDuaStatusSchema } from "./personal-dua";
import {
  hasUniqueValues,
  IsoTimestampSchema,
  RequiredShortTextSchema,
  ShortTextSchema,
  SortOrderSchema,
  UserTextSchema,
  UUIDSchema,
} from "./shared";

export const SessionTypeSchema = z.enum([
  "today_focus",
  "general",
  "tahajjud",
  "friday",
  "parents",
  "before_iftar",
  "arafah",
  "umrah",
  "travel",
  "custom",
]);

const SessionMutableShape = {
  name: RequiredShortTextSchema.max(120),
  description: UserTextSchema.max(2_000).optional(),
  type: SessionTypeSchema.default("custom"),
  icon: ShortTextSchema.max(80).optional(),
  contextTags: z.array(RequiredShortTextSchema.max(80)).max(50).refine(hasUniqueValues).default([]),
  sortOrder: SortOrderSchema.default(0),
  archivedAt: IsoTimestampSchema.optional(),
} satisfies z.ZodRawShape;

export const SessionCreateSchema = z.object(SessionMutableShape).strict();

export const SessionSchema = z
  .object({
    id: UUIDSchema,
    ...SessionMutableShape,
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    deletedAt: IsoTimestampSchema.optional(),
  })
  .strict();

export const SessionUpdateSchema = z
  .object({
    name: RequiredShortTextSchema.max(120).optional(),
    description: UserTextSchema.max(2_000).optional(),
    type: SessionTypeSchema.optional(),
    icon: ShortTextSchema.max(80).optional(),
    contextTags: z.array(RequiredShortTextSchema.max(80)).max(50).refine(hasUniqueValues).optional(),
    sortOrder: SortOrderSchema.optional(),
    archivedAt: IsoTimestampSchema.optional(),
  })
  .strict();

export const SessionBlockTypeSchema = z.enum([
  "today_focus",
  "collection",
  "person",
  "tag",
  "dua",
  "active_duas",
]);

export const SessionSelectionSchema = z.enum(["all", "rotating", "oldest_reviewed", "random"]);

const SessionBlockMutableObject = z.object({
  sessionId: UUIDSchema,
  type: SessionBlockTypeSchema,
  sourceId: UUIDSchema.optional(),
  label: ShortTextSchema.max(120).optional(),
  limit: z.number().int().positive().max(1_000).optional(),
  selection: SessionSelectionSchema.default("all"),
  excludedStatuses: z
    .array(PersonalDuaStatusSchema)
    .max(PersonalDuaStatusSchema.options.length)
    .refine(hasUniqueValues)
    .default(["archived"]),
  sortOrder: SortOrderSchema.default(0),
});

function validateBlockSource(
  block: { type: z.infer<typeof SessionBlockTypeSchema>; sourceId?: string | undefined },
  context: z.RefinementCtx,
): void {
  const needsSource = ["collection", "person", "tag", "dua"].includes(block.type);
  if (needsSource && block.sourceId === undefined) {
    context.addIssue({
      code: "custom",
      message: `${block.type} blocks require a sourceId.`,
      path: ["sourceId"],
    });
  }
  if (!needsSource && block.sourceId !== undefined) {
    context.addIssue({
      code: "custom",
      message: `${block.type} blocks cannot have a sourceId.`,
      path: ["sourceId"],
    });
  }
}

export const SessionBlockCreateSchema = SessionBlockMutableObject.strict().superRefine(validateBlockSource);

export const SessionBlockSchema = SessionBlockMutableObject.extend({
  id: UUIDSchema,
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
  deletedAt: IsoTimestampSchema.optional(),
  deletionReason: z.enum(["superseded", "session_deleted"]).optional(),
})
  .strict()
  .superRefine(validateBlockSource);

export const SessionBlockUpdateSchema = z
  .object({
    type: SessionBlockTypeSchema.optional(),
    sourceId: UUIDSchema.optional(),
    label: ShortTextSchema.max(120).optional(),
    limit: z.number().int().positive().max(1_000).optional(),
    selection: SessionSelectionSchema.optional(),
    excludedStatuses: z.array(PersonalDuaStatusSchema).refine(hasUniqueValues).optional(),
    sortOrder: SortOrderSchema.optional(),
  })
  .strict();

export type DuaSession = z.output<typeof SessionSchema>;
export type Session = DuaSession;
export type CreateSessionInput = z.input<typeof SessionCreateSchema>;
export type UpdateSessionInput = z.input<typeof SessionUpdateSchema>;
export type SessionBlock = z.output<typeof SessionBlockSchema>;
export type CreateSessionBlockInput = z.input<typeof SessionBlockCreateSchema>;
export type UpdateSessionBlockInput = z.input<typeof SessionBlockUpdateSchema>;
